import Typography from '@mui/material/Typography';
import { Editor } from '@tinymce/tinymce-react';
import { useEffect, useState } from 'react';
import type { EditorEvent, Editor as TinyMCEEditor } from 'tinymce';
import { FlexBox } from '../../components/layout';
import { useTinyMCEContext } from './TinyMCEContext';

interface HTMLEditorProps {
  apiKey?: string;
  limit?: number;
  value: string;
  disabled?: boolean;
  onChange?: (value: string) => void;
  onTextChange?: (text: string) => void;
  onTextLengthChange?: (length: number) => void;
  plugins?: string[];
  initProps?: {
    height?: number;
    toolbar?: string[];
  };
  label?: string;
  error?: boolean;
  helperText?: string;
}

const HTMLEditor: React.FC<HTMLEditorProps> = ({
  apiKey,
  limit = Infinity,
  value: initialValue,
  disabled = false,
  onChange,
  onTextChange,
  onTextLengthChange,
  plugins,
  initProps,
  label,
  error,
  helperText,
}) => {
  const context = useTinyMCEContext();
  const [value, setValue] = useState(initialValue);
  const [length, setLength] = useState(0);

  useEffect(() => {
    setValue(initialValue);
    setLength(initialValue.length);
  }, [initialValue]);

  const handleInit = (evt: EditorEvent<unknown>, editor: TinyMCEEditor) => {
    const text = editor.getContent({ format: 'text' });
    onTextChange?.(text);
    setLength(text.length);
    onTextLengthChange?.(text.length);
  };

  const handleUpdate = (value: string, editor: TinyMCEEditor) => {
    const text = editor.getContent({ format: 'text' });
    setValue(value);
    setLength(text.length);
    onChange?.(value);
    onTextChange?.(text);
    onTextLengthChange?.(text.length);
  };

  return (
    <FlexBox flexDirection="column" gap={0.5}>
      {label && (
        <Typography
          variant="caption"
          color={error ? 'error' : 'text.secondary'}
          sx={{ mb: 0.5 }}
        >
          {label}
        </Typography>
      )}
      <Editor
        apiKey={apiKey || context.apiKey}
        value={value}
        plugins={['lists', 'paste', 'link', ...(plugins || [])]}
        disabled={disabled}
        onInit={handleInit}
        onEditorChange={handleUpdate}
        init={{
          height: 300,
          menubar: false,
          toolbar: [
            'undo redo | formatselect | bold italic underline strikethrough | fontsize bullist numlist | removeformat',
            'outdent indent | alignleft aligncenter alignright alignjustify',
          ],
          ...initProps,
        }}
      />
      {limit !== Infinity && (
        <FlexBox justifyContent="flex-end">
          <Typography
            variant="caption"
            color={length > limit ? 'error' : 'text.secondary'}
          >
            {length} / {limit}
          </Typography>
        </FlexBox>
      )}
      {helperText && (
        <Typography
          variant="caption"
          color={error ? 'error' : 'text.secondary'}
        >
          {helperText}
        </Typography>
      )}
    </FlexBox>
  );
};

export default HTMLEditor;
